// Coach Portefeuille — carte de synthèse du brief quantitatif quotidien.
// Analyse locale déterministe, en lecture seule : AUCUN crédit IA débité
// (à la différence de la card Conseil / portfolio-coach/chat).
//   GET  /api/investment/coach/brief/  -> { date, payload, from_cache } | 404
//   POST /api/investment/coach/brief/  -> (re)génère le brief du jour
// La card affiche le régime, l'audit des stops et le haut de la shortlist ;
// le détail complet vit sur /investments/coach (detailTo du CardShell).
import React, { useEffect, useState } from 'react'
import { Box, Button, Chip, CircularProgress, Stack } from '@mui/material'
import { useTranslation } from 'react-i18next'
import QueryStatsOutlinedIcon from '@mui/icons-material/QueryStatsOutlined'
import RefreshOutlinedIcon from '@mui/icons-material/RefreshOutlined'
import CardHeader from '../../_shared/CardHeader'
import { invTheme } from '../../_shared/theme'
import { makeGetRequest, makePostRequest } from 'utils/api'

export const REGIME_COLORS = {
  RISK_ON: '#34d399',
  NEUTRE: '#f59e0b',
  RISK_OFF: '#ef4444',
}

export default function CoachCard() {
  const { t } = useTranslation('layout')
  const [brief, setBrief] = useState(null) // payload du dernier brief archivé
  const [loading, setLoading] = useState(true)
  const [generating, setGenerating] = useState(false)
  const [error, setError] = useState(null)

  useEffect(() => {
    const load = async () => {
      try {
        const { data, status } = await makeGetRequest('/api/investment/coach/brief/', {}, true)
        if (status === 200 && data?.payload) setBrief(data.payload)
      } catch (_) {
        /* 404 = pas encore de brief : état vide */
      } finally {
        setLoading(false)
      }
    }
    load()
  }, [])

  const generate = async () => {
    if (generating) return
    setGenerating(true)
    setError(null)
    try {
      const { data } = await makePostRequest('/api/investment/coach/brief/', {}, {}, true)
      if (data?.payload) setBrief(data.payload)
      else setError(t('investments.coach.card.error'))
    } catch (_) {
      setError(t('investments.coach.card.error'))
    } finally {
      setGenerating(false)
    }
  }

  const regime = brief?.regime
  const stops = brief?.garde_fous?.stops || []
  const cut = stops.filter((s) => (s.statut || '').startsWith('🔴')).length
  const watch = stops.filter((s) => (s.statut || '').startsWith('🟠')).length
  const levier = brief?.garde_fous?.levier
  const lignes = brief?.shortlist?.lignes || []
  const accent = REGIME_COLORS[regime?.etat] || invTheme.accents.market

  return (
    <Box display="flex" flexDirection="column" height="100%" minHeight={0}>
      <CardHeader
        icon={<QueryStatsOutlinedIcon fontSize="small" />}
        title={t('investments.coach.card.title')}
        subtitle={t('investments.coach.card.subtitle')}
        actions={
          brief ? (
            <Button
              data-noclick
              size="small"
              onClick={generate}
              disabled={generating}
              startIcon={
                generating ? (
                  <CircularProgress size={12} sx={{ color: 'inherit' }} />
                ) : (
                  <RefreshOutlinedIcon sx={{ fontSize: 15 }} />
                )
              }
              sx={{
                textTransform: 'none',
                fontSize: 11,
                color: 'rgba(255,255,255,0.7)',
                '&:hover': { color: '#63a0ff' },
              }}
            >
              {generating
                ? t('investments.coach.card.generating')
                : t('investments.coach.card.refresh')}
            </Button>
          ) : null
        }
      />

      <Box mt={2} flex={1} minHeight={0} display="flex" flexDirection="column">
        {loading ? (
          <Box flex={1} display="flex" alignItems="center" justifyContent="center">
            <CircularProgress size={22} sx={{ color: invTheme.accents.market }} />
          </Box>
        ) : !brief ? (
          // État vide : premier brief à générer (gratuit, local).
          <Box
            flex={1}
            display="flex"
            flexDirection="column"
            justifyContent="center"
            alignItems="center"
            textAlign="center"
            px={1}
          >
            <Box sx={{ fontSize: 14, lineHeight: 1.55, color: 'rgba(255,255,255,0.85)' }}>
              {t('investments.coach.card.empty')}
            </Box>
            <Button
              data-noclick
              onClick={generate}
              variant="outlined"
              size="small"
              disabled={generating}
              startIcon={
                generating ? (
                  <CircularProgress size={14} sx={{ color: 'inherit' }} />
                ) : (
                  <QueryStatsOutlinedIcon />
                )
              }
              sx={{
                mt: 2.5,
                color: invTheme.accents.market,
                borderColor: 'rgba(96,165,250,0.45)',
                textTransform: 'none',
                fontWeight: 600,
                '&:hover': {
                  borderColor: invTheme.accents.market,
                  backgroundColor: 'rgba(96,165,250,0.10)',
                },
              }}
            >
              {generating
                ? t('investments.coach.card.generating')
                : t('investments.coach.card.generate')}
            </Button>
            <Box sx={{ mt: 1.25, fontSize: 11, color: 'rgba(255,255,255,0.45)' }}>
              {t('investments.coach.card.freeHint')}
            </Box>
            {error && <Box sx={{ mt: 1, color: '#f87171', fontSize: 12 }}>{error}</Box>}
          </Box>
        ) : (
          <>
            {/* Régime de marché */}
            <Box
              sx={{
                p: 1.5,
                borderRadius: 2,
                border: `1px solid ${accent}44`,
                background: `radial-gradient(circle at 30% 0%, ${accent}1f, transparent 65%)`,
              }}
            >
              <Stack direction="row" alignItems="center" spacing={1}>
                <Chip
                  size="small"
                  label={regime?.etat || '—'}
                  sx={{
                    fontWeight: 700,
                    letterSpacing: 0.5,
                    color: '#001b42',
                    backgroundColor: accent,
                  }}
                />
                <Box sx={{ fontSize: 12, color: 'rgba(255,255,255,0.7)' }}>
                  {regime?.buy_allowed
                    ? t('investments.coach.detail.buyAllowed')
                    : t('investments.coach.detail.buyBlocked')}
                  {regime?.risk_multiplier !== undefined && ` · ×${regime.risk_multiplier}`}
                </Box>
              </Stack>
              <Box sx={{ mt: 1, fontSize: 12.5, lineHeight: 1.5, color: 'rgba(255,255,255,0.75)' }}>
                {regime?.commentaire}
              </Box>
            </Box>

            {/* Garde-fous : stops + levier */}
            <Box sx={{ mt: 1.25, fontSize: 12.5, color: 'rgba(255,255,255,0.8)' }}>
              {cut > 0 && (
                <Box sx={{ color: '#f87171', fontWeight: 600 }}>
                  🔴 {cut} {t('investments.coach.card.stopsAlert')}
                </Box>
              )}
              {watch > 0 && (
                <Box sx={{ color: '#fbbf24' }}>
                  🟠 {watch} {t('investments.coach.card.stopsWatch')}
                </Box>
              )}
              {cut === 0 && watch === 0 && (
                <Box sx={{ color: invTheme.positive }}>
                  🟢 {t('investments.coach.card.stopsOk')}
                </Box>
              )}
              {levier?.alerte && (
                <Box sx={{ color: '#f87171' }}>
                  ⚠️ {t('investments.coach.card.leverageAlert')} ({levier.exposition_pct} % &gt;{' '}
                  {levier.seuil_pct} %)
                </Box>
              )}
            </Box>

            {/* Haut de la shortlist */}
            <Box sx={{ mt: 1.25, flex: 1, minHeight: 0, overflowY: 'auto' }}>
              <Box sx={{ ...invTheme.subtitle, mb: 0.5 }}>
                {t('investments.coach.card.shortlistTop')}
              </Box>
              {lignes.length === 0 ? (
                <Box sx={{ fontSize: 12.5, color: 'rgba(255,255,255,0.6)' }}>
                  {brief?.shortlist?.message || t('investments.coach.card.shortlistEmpty')}
                </Box>
              ) : (
                lignes.slice(0, 4).map((l) => (
                  <Box
                    key={l.ticker}
                    display="flex"
                    alignItems="baseline"
                    sx={{ py: 0.5, borderBottom: invTheme.rowDivider, fontSize: 12.5 }}
                  >
                    <Box sx={{ fontWeight: 600, color: 'white', mr: 1 }}>{l.ticker}</Box>
                    <Box
                      sx={{
                        color: 'rgba(255,255,255,0.55)',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        flex: 1,
                      }}
                    >
                      {l.nom}
                    </Box>
                    <Box sx={{ ml: 1, fontWeight: 700, color: invTheme.accents.market }}>
                      {l.score_composite}
                    </Box>
                  </Box>
                ))
              )}
            </Box>

            <Box sx={{ mt: 1, fontSize: 11, color: 'rgba(255,255,255,0.4)' }}>
              {t('investments.coach.card.briefOf')} {brief.date}
              {error && <Box component="span" sx={{ color: '#f87171', ml: 1 }}>{error}</Box>}
            </Box>
          </>
        )}
      </Box>
    </Box>
  )
}
